import React, { useState, useEffect } from "react";
import { useTheme } from "@repo/shared";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { confirmAlert } from 'react-confirm-alert';
import { Container, Card, Form, Row, Col, Button, Table, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import StudentDataService from "../../services/student/StudentService";
import SchoolDataService from "../../services/school/SchoolService";
import ConfigDataService from "../../services/config/ConfigService";
import ParentDataService from "../../services/parent/ParentService";
import LoadingSpinner from '../common/LoadingSpinner';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Import React Bootstrap components for a modern layout

const Student = props => {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parentId = searchParams.get("parentId");
  const history = useHistory();
  const initialStudentState = StudentDataService.initialiseNewStudent();
  const [currentStudent, setCurrentStudent] = useState(initialStudentState);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [updateMode, setUpdateMode] = useState(false);
  const [schools, setSchools] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [parents, setParents] = useState([]);
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { appTheme } = useTheme();

  useEffect(async () => {
    console.log("Parent ID:", parentId);

    const incomingParents = (await ParentDataService.getParents()).parents;
    setParents(incomingParents);

    if (parentId) {
      const incomingParent = incomingParents.find(p => p.id === parentId);
      if (incomingParent && incomingParent.email) {
        setParent(incomingParent);
        setCurrentStudent({ ...currentStudent, parentId: incomingParent.id });
        console.log('set parent to:', incomingParent);
      }
    }

    if (id) {
      setUpdateMode(true);

      const incomingStudent = await StudentDataService.get(id);
      setCurrentStudent(incomingStudent);

      const incomingParent = incomingParents.find(p => p.id === incomingStudent.parentId);
      setParent(incomingParent);
      console.log('set parent to:', incomingParent);
    }

    setLoading(false);
  }, [parentId, id]);

  useEffect(() => {

    document.title = `${appTheme?.appName || ''} - Student`;

    SchoolDataService.getAll()
      .then(setSchools)
      .catch(console.error);

    ConfigDataService.getSemesterDates()
      .then(setSchoolYears)
      .catch(console.error);


  }, []);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setCurrentStudent({ ...currentStudent, [name]: value });
  };

  const handleEnrollmentChange = event => {
    const { checked, name } = event.target;
    let enrolments = [...currentStudent.enrolments];

    if (!checked) {
      enrolments = enrolments.filter(e => e.schoolYear !== name);
    } else {
      enrolments.push({
        schoolYear: name,
        studentId: currentStudent.id,
        notes: '',
      });
    }
    setCurrentStudent({ ...currentStudent, enrolments });
  };

  const handleSchoolSelection = event => {
    const { value } = event.target;
    const selectedSchool = schools.find(s => s.id === value) || { id: 0 };
    setCurrentStudent({ ...currentStudent, school: selectedSchool });
  };

  const handleParentSelection = selectedOption => {
    if (selectedOption) {
      const selectedParent = parents.find(p => p.id === selectedOption.value);
      if (selectedParent) {
        setCurrentStudent({ ...currentStudent, parentId: selectedParent.id });
        setParent(selectedParent);
      }
    } else {
      setCurrentStudent({ ...currentStudent, parentId: null });
      setParent(null);
    }
  };

  const validate = () => {
    const errorFields = [];
    if (!currentStudent.school || currentStudent.school.id === 0 || currentStudent.school.id === '5cb528e9-7f93-4463-8b1b-036425c33d47') {
      errorFields.push('School');
    }
    if (!currentStudent.firstName) errorFields.push('First Name');
    if (!currentStudent.lastName) errorFields.push('Last Name');
    if (!currentStudent.parentId) errorFields.push('Parent');

    if (errorFields.length) {
      setErrorMessage(`These fields may not be blank: ${errorFields.join(', ')}`);
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const saveStudent = (andNew = false) => {
    if (validate()) {
      setSaving(true);
      StudentDataService.create(currentStudent)
        .then(() => {
          setMessage("The Student was saved successfully!");
          setSaving(false);
          if (andNew) {
            setCurrentStudent(StudentDataService.initialiseNewStudent());
          } else {
            history.goBack();
          }
        })
        .catch((err) => {
          console.error(err);
          setSaving(false);
        });
    }
  };

  const updateStudent = () => {
    if (validate()) {
      setSaving(true);
      StudentDataService.update(currentStudent.id, currentStudent)
        .then(() => {
          setMessage("The Student was updated successfully!");
          setSaving(false);
          history.goBack();
        })
        .catch((err) => {
          console.error(err);
          setSaving(false);
        });
    }
  };

  const deleteStudent = () => {
    confirmAlert({
      title: 'Delete Student',
      message: `Are you sure you want to delete ${currentStudent.firstName} ${currentStudent.lastName}?`,
      buttons: [{
        label: 'Yes',
        onClick: () => {
          setSaving(true);
          StudentDataService.remove(currentStudent.id)
            .then(() => {
              setSaving(false);
              history.push("/students");
            })
            .catch((err) => {
              console.error(err);
              setSaving(false);
            });
        }
      }, { label: 'No' }]
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage("Email copied to clipboard!");
      setTimeout(() => setMessage(""), 2000);
    }).catch(() => {
      setErrorMessage("Failed to copy email");
      setTimeout(() => setErrorMessage(""), 2000);
    });
  };

  const renderBody = () => {
    return (
      <>
        {/* MODERNIZED: Replaced <form> with React Bootstrap's <Form> */}
        <Form>
          {/* MODERNIZED: Using a responsive grid (Row/Col) for the layout */}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>First Name (*)</Form.Label>
                <Form.Control type="text" name="firstName" value={currentStudent.firstName} onChange={handleInputChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Last Name (*)</Form.Label>
                <Form.Control type="text" name="lastName" value={currentStudent.lastName} onChange={handleInputChange} />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>School (*)</Form.Label>
                <Form.Select name="school" value={currentStudent.school?.id} onChange={handleSchoolSelection}>
                  <option value="0">- Select School -</option>
                  {schools.map(school =>
                    <option key={school.id} value={school.id}>
                      {`${school.name}${school.kindergarten ? ' (kg)' : ''}`}
                    </option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} sm={6}>
              <Form.Group>
                <Form.Label>Year</Form.Label>
                <Form.Select name="year" value={currentStudent.year} onChange={handleInputChange}>
                  <option value="">- Select Year -</option>
                  {['1', '2', '3', '4', '5', '6', '7'].map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} sm={6}>
              <Form.Group>
                <Form.Label>Class Name</Form.Label>
                <Form.Control type="text" name="className" value={currentStudent.className} onChange={handleInputChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Age</Form.Label>
                <Form.Control type="number" name="age" value={currentStudent.age} onChange={handleInputChange} />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Parent (*)</Form.Label>
                <Select
                  options={parents.filter(p => p.email).map(p => ({ value: p.id, label: `${p.firstName} ${p.lastName} (${p.email})` }))}
                  value={parent?.id ? { value: parent.id, label: parent ? `${parent.firstName} ${parent.lastName} (${parent.email})` : '' } : null}
                  onChange={handleParentSelection}
                  isClearable
                  isSearchable
                  placeholder="Select a parent..."
                />
              </Form.Group>
            </Col>

            {parent && (
              <Col md={12}>
                <div className="p-2 bg-light rounded border border-light-subtle d-flex justify-content-between align-items-start" style={{ fontSize: '0.8rem' }}>
                  <div className="text-muted">
                    <div className="mb-1">
                      <strong>Parent:</strong> {parent.firstName} {parent.lastName} • {parent.email}
                      {parent.phoneNumber && <> • {parent.phoneNumber}</>}
                    </div>
                    {parent.address && (
                      <div>
                        <strong>Address:</strong> {parent.address}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => copyToClipboard(parent.email)}
                    className="ms-2"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Copy Email
                  </Button>
                </div>
              </Col>
            )}

            <Col md={12}>
              <Form.Group>
                <Form.Label>General Notes</Form.Label>
                <Form.Control as="textarea" rows={3} name="notes" value={currentStudent.notes} onChange={handleInputChange} />
              </Form.Group>
            </Col>
          </Row>
        </Form>

        {/* MODERNIZED: Enrolments section is now a styled Card */}
        <Card className="mt-4">
          <Card.Header as="h5">Enrolments</Card.Header>
          <Card.Body>
            {schoolYears.map(s => {
              const thisEnrolment = currentStudent.enrolments.find(e => e.schoolYear === s.year);
              return (
                <div key={s.year} className="p-3 mb-3 border rounded">
                  <Form.Check
                    type="checkbox"
                    id={s.year}
                    name={s.year}
                    label={<strong>{s.year}</strong>}
                    checked={!!thisEnrolment}
                    onChange={handleEnrollmentChange}
                  />
                  {thisEnrolment && (
                    <div className="mt-2 ps-3">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enrolment notes..."
                        value={thisEnrolment.notes}
                        onChange={e => {
                          thisEnrolment.notes = e.target.value;
                          setCurrentStudent({ ...currentStudent });
                        }}
                      />
                      <Row className="mt-2">
                        <Col sm={6}>
                          <Form.Check
                            type="checkbox"
                            label="Paid Winter"
                            checked={!!thisEnrolment.paidTimestampWinter}
                            onChange={e => {
                              thisEnrolment.paidTimestampWinter = e.target.checked ? Date.now() : null;
                              setCurrentStudent({ ...currentStudent });
                            }}
                          />
                        </Col>
                        <Col sm={6}>
                          <Form.Check
                            type="checkbox"
                            label="Paid Summer"
                            checked={!!thisEnrolment.paidTimestampSummer}
                            onChange={e => {
                              thisEnrolment.paidTimestampSummer = e.target.checked ? Date.now() : null;
                              setCurrentStudent({ ...currentStudent });
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              );
            })}
          </Card.Body>
        </Card>

        {/* MODERNIZED: Tables are in Cards and are responsive */}
        {updateMode && (
          <>
            <Card className="mt-4">
              <Card.Header as="h5">Allocated Classes</Card.Header>
              <Card.Body>
                {currentStudent?.classes?.length ? (
                  <Table responsive striped bordered hover size="sm">
                    <thead>
                      <tr><th>Title</th><th>Year</th><th>Semester</th><th>Day</th><th>Time</th></tr>
                    </thead>
                    <tbody>
                      {currentStudent.classes
                        .sort((a, b) => `${b.schoolYear}${b.semester}`.localeCompare(`${a.schoolYear}${a.semester}`))
                        .map((c, i) => (
                          <tr key={i}>
                            <td>{c.title}</td><td>{c.schoolYear}</td><td>{c.semester}</td><td>{c.day}</td><td>{c.time}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                ) : <p className="text-muted">No classes allocated.</p>}
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Header as="h5">Attending Events</Card.Header>
              <Card.Body>
                {currentStudent?.events?.length ? (
                  <Table responsive striped bordered hover size="sm">
                    <thead>
                      <tr><th>Title</th><th>Paid</th></tr>
                    </thead>
                    <tbody>
                      {currentStudent.events.map(event => (
                        <tr key={event.id}>
                          <td>{event.title}</td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={!!event.paidTimestamp}
                              onChange={e => {
                                event.paidTimestamp = e.target.checked ? Date.now() : null;
                                setCurrentStudent({ ...currentStudent });
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : <p className="text-muted">No events attended.</p>}
              </Card.Body>
            </Card>
          </>
        )}

        <hr className="my-4" />

        {/* MODERNIZED: Button row is cleaner and responsive */}
        <div className="d-flex flex-wrap gap-2">
          {updateMode ? (
            <Button variant="primary" onClick={updateStudent} disabled={saving}>
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          ) : (
            <>
              <Button variant="primary" onClick={() => saveStudent(false)} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button variant="success" onClick={() => saveStudent(true)} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save & Add New"
                )}
              </Button>
            </>
          )}
          <Button variant="outline-secondary" onClick={() => history.goBack()} disabled={saving}>Cancel</Button>
          {updateMode && (
            <Button variant="danger" className="ms-auto" onClick={deleteStudent} disabled={saving}>
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>

        {message && <p className="mt-3 text-success">{message}</p>}
        {errorMessage && <p className="mt-3 text-danger">{errorMessage}</p>}
      </>
    );
  };

  return (
    <Container className="my-4">
      {/* MODERNIZED: Page is wrapped in a main Card for a modern look */}
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <h3 className="mb-4">{updateMode ? "Update Student" : "Add Student"}</h3>
          {loading ? <div className='loadingSpinner'><LoadingSpinner /></div> : renderBody()}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Student;